import { Text, Center, Spinner, Box, Flex, HStack } from '@chakra-ui/react';
import {
  VideoHangUpIcon,
  VoiceJoinIcon,
  VideoIcon,
  Avatar,
  CallWarningYellowIcon,
} from '@halo/ui-chat-react';
import { Drawer, Portal } from '@halo/ui-react';
import { useTranslation } from 'react-i18next';
import { MyTrans } from '@halo/ui-react';

enum MediaActionType {
  AUDIO = 1,
  VIDEO = 2,
}

export interface CallNewJoinProps {
  isOpen?: boolean;
  mediaType: MediaActionType;
  avatar?: string;
  name?: string;
  callMembers?: Record<string, any>[];
  setIsOpen?: (open: boolean) => void;
  onAccept?: (actionType: MediaActionType) => void;
  onDecline?: () => void;
}

export function CallNewJoin({
  isOpen,
  mediaType,
  avatar,
  name,
  callMembers,
  setIsOpen,
  onAccept,
  onDecline,
}: CallNewJoinProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(isOpen);
  const [loading, setLoading] = useState(false);

  const callMembersLength = useMemo(() => callMembers?.length ?? 0, [callMembers]);

  const onOpenChange = ({ open }: { open: boolean }) => {
    setOpen(open);
    setIsOpen?.(open);
  };

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  return (
    <Drawer.Root
      open={open}
      onOpenChange={onOpenChange}
      closeOnInteractOutside={false}
      modal={false}
    >
      <Portal>
        <Drawer.Positioner
          pointerEvents="none"
          top="20"
          right="20"
          w={'calc(100% - 20px)'}
          h="auto"
        >
          <Drawer.Content borderRadius="2xl" maxW="450px" w="auto" overflow="hidden">
            <Box>
              <Box p="4">
                <Flex justifyContent="space-between">
                  <Flex gap="2">
                    <Avatar src={avatar} name={name} />
                    <Box>
                      <Text fontWeight="medium" fontSize="xs" color="grey.95" lineHeight="1.3">
                        <MyTrans
                          i18nKey={
                            mediaType === MediaActionType.VIDEO
                              ? 'calls.inviteYouForVideoCall'
                              : 'calls.inviteYouForAudioCall'
                          }
                          components={{
                            name: <Text as="span" fontWeight="semibold" fontSize="md" />,
                          }}
                          values={{
                            name: name || '',
                          }}
                        />
                      </Text>
                    </Box>
                  </Flex>
                  <Flex gap="2.5">
                    {loading ? (
                      <Center>
                        <Spinner color="#00B600" size="lg" />
                      </Center>
                    ) : (
                      <>
                        <Box
                          width="9"
                          cursor="pointer"
                          _active={{ opacity: 0.7 }}
                          onClick={() => {
                            onAccept?.(MediaActionType.AUDIO);
                            setLoading(true);
                          }}
                        >
                          <VoiceJoinIcon />
                        </Box>
                        {mediaType === MediaActionType.VIDEO && (
                          <Box
                            width="9"
                            cursor="pointer"
                            _active={{ opacity: 0.7 }}
                            onClick={() => {
                              onAccept?.(MediaActionType.VIDEO);
                              setLoading(true);
                            }}
                          >
                            <VideoIcon />
                          </Box>
                        )}

                        <Box
                          width="9"
                          cursor="pointer"
                          _active={{ opacity: 0.7 }}
                          onClick={onDecline}
                        >
                          <VideoHangUpIcon />
                        </Box>
                      </>
                    )}
                  </Flex>
                </Flex>
                {callMembersLength > 0 && (
                  <Box mt="3">
                    <Text
                      fontWeight="normal"
                      fontSize="xs"
                      opacity="0.8"
                      lineHeight="normal"
                      mb="1.5"
                    >
                      {t('calls.othersCalled', { count: callMembersLength })}
                    </Text>
                    <HStack alignItems="center" gap="1">
                      {callMembers?.slice(0, 5)?.map((member: any) => (
                        <Avatar src={member?.avatar as string} key={member?.userId} w="6" h="6" />
                      ))}
                      {callMembersLength > 5 && (
                        <Flex
                          alignItems="center"
                          justifyContent="center"
                          px="1.5"
                          zIndex={10}
                          bg="blackAlpha.30"
                          rounded="14px"
                          fontSize="xs"
                          h="6"
                          boxSizing="border-box"
                          color="whiteAlpha.100"
                        >
                          +{callMembersLength > 104 ? 99 : callMembersLength - 5}
                        </Flex>
                      )}
                    </HStack>
                  </Box>
                )}
              </Box>

              <Flex
                gap="1.5"
                px="2"
                py="1.5"
                bg="#fff5e5"
                fontWeight="medium"
                alignItems="center"
              >
                <CallWarningYellowIcon /> {t('calls.answerNewCallWarning')}
              </Flex>
            </Box>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
}

export default memo(CallNewJoin);
